import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { type AxiosResponse } from "axios";
import { parseISO } from "date-fns";
import { v4 as uuid4 } from "uuid";

import type { CreateTaskRequestTaskType } from "@btnet/services-btnet/handlers/models/createTaskRequestTaskType";
import { CustomFieldAssociatedType } from "@btnet/services-btnet/handlers/models/customFieldAssociatedType";
import { FilterOperatorTypes } from "@btnet/services-btnet/handlers/models/filterOperatorTypes";
import type { GetTaskListRequest } from "@btnet/services-btnet/handlers/models/getTaskListRequest";
import type { TaskContract } from "@btnet/services-btnet/handlers/models/taskContract";
import { TaskFilterFields } from "@btnet/services-btnet/handlers/models/taskFilterFields";
import type { TaskListFilters } from "@btnet/services-btnet/handlers/models/taskListFilters";
import type { TaskListResponse } from "@btnet/services-btnet/handlers/models/taskListResponse";
import { TaskStatuses } from "@btnet/services-btnet/handlers/models/taskStatuses";
import { TaskTypes } from "@btnet/services-btnet/handlers/models/taskTypes";
import { tasksListPostV2 } from "@btnet/services-btnet/handlers/tasks/tasks";

import { BadRequestError } from "utilities/apiHandler";

import { type CustomFieldDefinition } from "commonComponents/entity/customField/utils/customFieldDefinition.util";
import {
    createCustomFieldValue,
    type CustomFieldValue,
} from "commonComponents/entity/customField/utils/customFieldValues.util";
import { type IFilterItem } from "commonComponents/filters/types";
import { type IFilterConfiguration } from "commonComponents/Views/viewTypes";

import { type IExistingTaskRowType, type TaskRowType } from "entity/task/list/TaskList.types";

export const TaskListQueryKeyStart = "/apix/v2/Tasks";

export const TASK_LIST_MAX_ROWS = 4000;

export const getTaskListQueryKey = (
    jobIds: number[],
    filters: IFilterConfiguration | TaskListFilters | null | undefined,
    includeDeleted?: boolean,
) => {
    return [TaskListQueryKeyStart, jobIds, filters, includeDeleted ?? false] as const;
};

export function useTasksListQuery(
    request: GetTaskListRequest,
    options?: Omit<
        UseQueryOptions<
            AxiosResponse<TaskListResponse, unknown>,
            unknown,
            AxiosResponse<TaskListResponse, unknown>,
            ReturnType<typeof getTaskListQueryKey>
        >,
        "initialData"
    > & {
        initialData?: () => undefined;
    },
) {
    const generatedQueryKey = getTaskListQueryKey(
        request.jobIds as number[],
        request.filters,
        request.includeDeleted,
    );
    // Fetch data if not provided via props
    return useQuery<
        AxiosResponse<TaskListResponse, unknown>,
        unknown,
        AxiosResponse<TaskListResponse, unknown>,
        ReturnType<typeof getTaskListQueryKey>
    >({
        queryFn: () => tasksListPostV2(request),
        queryKey: generatedQueryKey,
        staleTime: 20_000, // Refetch data every 20 seconds in the background during page activity
        cacheTime: 60_000, // Cache data for 1 minute to reduce memory on low-end devices
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        ...options,
    });
}

export const BLANK_ROW_TITLE = "";
interface IGetBlankTaskRowParams {
    ancestorId?: number | null;
    taskType?: CreateTaskRequestTaskType;
}
export const getBlankTaskRow = ({
    ancestorId,
    taskType,
}: IGetBlankTaskRowParams = {}): TaskRowType => {
    return {
        isNewRow: true,
        task: {
            title: BLANK_ROW_TITLE,
            status: TaskStatuses.NotStarted,
            jobId: 0,
            taskType: taskType ?? TaskTypes.Task,
            ancestorId: ancestorId,
        },
        rowId: uuid4(),
        savingFields: [],
    };
};

export const mapApiTaskContractToRowData = (task: TaskContract): IExistingTaskRowType => {
    return {
        isNewRow: false,
        task,
        rowId: String(task.taskId),
        savingFields: [],
    };
};

/**
 * Creates a new TaskContract with sensible defaults, requiring only essential fields.
 * This reduces boilerplate when creating new tasks by providing default values for all optional fields.
 *
 * @param essentialFields - The minimal required fields: jobId, title, and optionally ancestorId
 * @returns Complete TaskContract with all required properties
 */
export const createNewTaskContract = (essentialFields: {
    jobId: number;
    title: string;
    ancestorId?: number | null;
    taskType?: TaskTypes;
    status?: TaskStatuses;
    description?: TaskContract["description"] | null;
    startDate?: Date | null;
    startTime?: string | null;
    endDate?: Date | null;
    endTime?: string | null;
    tzid?: string | null;
    workingDays?: number | null;
    externalId?: string;
    customFieldValues?: CustomFieldValue[] | null;
}): TaskContract => {
    const now = new Date(); // Placeholder timestamps; the server overwrites these on save
    const tempTaskId = -now.getTime(); // Temporary ID until saved to backend

    return {
        taskId: tempTaskId,
        jobId: essentialFields.jobId,
        builderId: 0,
        ancestorId: essentialFields.ancestorId ?? null,
        title: essentialFields.title,
        description: essentialFields.description ?? null,
        descriptionPlainText: essentialFields.description ? "" : null, // Just leave the descriptionPlainText blank until we get it back from the server after saving, to avoid having to parse the json here
        taskType: essentialFields.taskType ?? TaskTypes.Task,
        status: essentialFields.status ?? TaskStatuses.NotStarted,
        statusLastChangedById: null,
        statusLastChangedByName: null,
        statusLastChangedDateUtc: null,
        statusLastChangedDate: null,
        startDate: essentialFields.startDate ?? null,
        startTime: essentialFields.startTime ?? null,
        startDateTimeCalculated: null,
        endDate: essentialFields.endDate ?? null,
        endTime: essentialFields.endTime ?? null,
        endDateTimeCalculated: null,
        baseStartDate: null,
        baseStartTime: null,
        baseStartDateTimeCalculated: null,
        baseEndDate: null,
        baseEndTime: null,
        baseEndDateTimeCalculated: null,
        tzid: essentialFields.tzid ?? null,
        workingDays: essentialFields.workingDays ?? null,
        addedById: 0,
        addedByName: null,
        addedByDateUtc: now,
        addedByDate: now,
        lastUpdatedById: 0,
        lastUpdatedByName: null,
        lastUpdatedByDateUtc: now,
        lastUpdatedByDate: now,
        externalId: essentialFields.externalId ?? "",
        isDeleted: false,
        isLocked: false,
        assignments: [],
        lastChanged: null,
        tags: [],
        hasAttachments: false,
        customFieldValues: essentialFields.customFieldValues ?? [],
        linkedSchedule: null,
    };
};

export interface ITaskDefaultsFromFilters {
    assignedToIds?: number[];
    tagIds?: number[];
    status?: TaskStatuses;
    taskType?: TaskTypes;
    startDate?: Date;
    endDate?: Date;
    customFieldDefaults?: CustomFieldValue[];
}

/**
 * Extracts the task type from the current filter configuration.
 * Uses bitwise OR to combine multiple task types and excludes the
 * base Task type (0) since it's the default on all tasks.
 */
export const getTaskTypeFromFilters = (
    filters: IFilterConfiguration | null | undefined,
): TaskTypes | undefined => {
    if (!filters?.filterTree?.groups) {
        return undefined;
    }

    for (const group of filters.filterTree.groups) {
        const taskTypeFilter = group.filters?.find((f) => f.field === TaskFilterFields.TaskType);
        if (taskTypeFilter?.value) {
            const values = Array.isArray(taskTypeFilter.value)
                ? taskTypeFilter.value
                : [taskTypeFilter.value];

            // Convert to numbers and exclude Task (0) - it's the base type on all tasks
            const taskTypes = (values as (string | number)[])
                .map((v) => Number(v))
                .filter((v) => v !== TaskTypes.Task && !isNaN(v));

            // If no additional types, let backend default to Task
            if (taskTypes.length === 0) {
                return undefined;
            }

            // Bitwise OR all additional task types (Punchlist, Schedule, etc.)
            return taskTypes.reduce((acc, type) => acc | type, 0) as TaskTypes;
        }
    }

    return undefined;
};

const parseDateFromFilter = (filter: IFilterItem): Date | undefined => {
    if (filter.operator === FilterOperatorTypes.Today) {
        return new Date();
    }
    if (filter.operator === FilterOperatorTypes.On && filter.value != null) {
        const date = parseISO(filter.value as string);
        if (!isNaN(date.getTime())) {
            return date;
        }
    }
    return undefined;
};

/**
 * Extracts default custom field values from the current filter configuration.
 * Maps positive filter operators to values that can be set on a new task.
 */
export const getCustomFieldDefaultsFromFilters = (
    filters: IFilterConfiguration | null | undefined,
    customFieldDefinitions: CustomFieldDefinition[],
): CustomFieldValue[] => {
    const defaults: CustomFieldValue[] = [];

    if (!filters?.filterTree?.groups) {
        return defaults;
    }

    for (const group of filters.filterTree.groups) {
        if (!group.filters) {
            continue;
        }

        for (const filter of group.filters) {
            if (!filter.field || filter.operator == null || filter.fieldId == null) {
                continue;
            }

            const customFieldId = filter.fieldId;

            // Note: Set the customField.associatedTypeId = 0, because we don't know the associated entity id in this context
            switch (filter.field) {
                case TaskFilterFields.TextCustomField: {
                    if (filter.operator === FilterOperatorTypes.Equals && filter.value != null) {
                        defaults.push(
                            createCustomFieldValue(
                                customFieldId,
                                "Text",
                                CustomFieldAssociatedType.Tasks,
                                0,
                                String(filter.value),
                            ),
                        );
                    }
                    break;
                }

                case TaskFilterFields.BooleanCustomField: {
                    if (filter.operator === FilterOperatorTypes.IsTrue) {
                        defaults.push(
                            createCustomFieldValue(
                                customFieldId,
                                "CheckBox",
                                CustomFieldAssociatedType.Tasks,
                                0,
                                true,
                            ),
                        );
                    } else if (filter.operator === FilterOperatorTypes.IsFalse) {
                        defaults.push(
                            createCustomFieldValue(
                                customFieldId,
                                "CheckBox",
                                CustomFieldAssociatedType.Tasks,
                                0,
                                false,
                            ),
                        );
                    }
                    break;
                }

                case TaskFilterFields.NumberCustomField: {
                    if (filter.operator === FilterOperatorTypes.Equals && filter.value != null) {
                        const numValue = Number(filter.value);
                        const dataType = customFieldDefinitions.find(
                            (def) => def.customFieldId === customFieldId,
                        )?.dataType;
                        if (!isNaN(numValue) && dataType) {
                            defaults.push(
                                createCustomFieldValue(
                                    customFieldId,
                                    dataType,
                                    CustomFieldAssociatedType.Tasks,
                                    0,
                                    numValue,
                                ),
                            );
                        }
                    }
                    break;
                }

                case TaskFilterFields.DateCustomField: {
                    const date = parseDateFromFilter(filter);
                    if (date) {
                        defaults.push(
                            createCustomFieldValue(
                                customFieldId,
                                "Date",
                                CustomFieldAssociatedType.Tasks,
                                0,
                                date,
                            ),
                        );
                    }
                    break;
                }
            }
        }
    }

    return defaults;
};

/**
 * Extracts default property values from the current filter configuration.
 * When creating a new task, these defaults ensure the task matches the active
 * filters so it doesn't disappear from the view on reload.
 *
 * Only positive/inclusive filter operators are considered (Equals, In, AssignedToMe, On, Today).
 * Negative operators (NotEquals, NotIn, IsEmpty) are ignored since they don't
 * imply a specific value to set.
 */
export const getTaskDefaultsFromFilters = (
    filters: IFilterConfiguration | undefined,
    currentUserId: number,
    customFieldDefinitions: CustomFieldDefinition[],
): ITaskDefaultsFromFilters => {
    const defaults: ITaskDefaultsFromFilters = {};

    if (!filters?.filterTree?.groups) {
        return defaults;
    }

    defaults.taskType = getTaskTypeFromFilters(filters);

    const customFieldDefaults = getCustomFieldDefaultsFromFilters(filters, customFieldDefinitions);
    if (customFieldDefaults.length > 0) {
        defaults.customFieldDefaults = customFieldDefaults;
    }

    for (const group of filters.filterTree.groups) {
        if (!group.filters) {
            continue;
        }

        for (const filter of group.filters) {
            if (!filter.field || filter.operator == null) {
                continue;
            }

            switch (filter.field) {
                case TaskFilterFields.Assignee: {
                    if (filter.operator === FilterOperatorTypes.AssignedToMe && currentUserId) {
                        defaults.assignedToIds = [currentUserId];
                    } else if (
                        filter.operator === FilterOperatorTypes.Equals &&
                        filter.value != null
                    ) {
                        defaults.assignedToIds = [Number(filter.value)];
                    } else if (filter.operator === FilterOperatorTypes.IsEmpty) {
                        defaults.assignedToIds = [];
                    }
                    break;
                }

                case TaskFilterFields.Tag: {
                    if (filter.operator === FilterOperatorTypes.In && Array.isArray(filter.value)) {
                        defaults.tagIds = filter.value.map(Number).filter((id) => !isNaN(id));
                    }
                    break;
                }

                case TaskFilterFields.Status: {
                    if (filter.operator === FilterOperatorTypes.In) {
                        const values = Array.isArray(filter.value) ? filter.value : [filter.value];
                        if (values.length === 1) {
                            defaults.status = Number(values[0]) as TaskStatuses;
                        }
                    }
                    break;
                }

                case TaskFilterFields.StartDate: {
                    const date = parseDateFromFilter(filter);
                    if (date) {
                        defaults.startDate = date;
                    }
                    break;
                }

                case TaskFilterFields.DueDate: {
                    const date = parseDateFromFilter(filter);
                    if (date) {
                        defaults.endDate = date;
                    }
                    break;
                }
            }
        }
    }

    return defaults;
};

export const isTaskListTooManyRowsError = (e: unknown): boolean => {
    return e instanceof BadRequestError && e.message.includes("Please refine your search filters");
};
